import axios from "axios";
import { STATE_CITY } from "./stateCityMapping";

const compact = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = String(value).trim();
  if (!text || text === "—" || text === "-") return "";
  return text;
};

const pickFirst = (...values) => {
  for (const value of values) {
    const text = compact(value);
    if (text !== "") return text;
  }
  return "";
};

const normalizeNameKey = (str = "") =>
  String(str)
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const matchCityInState = (state, nCity) => {
  if (!nCity || !state?.cities) return "";
  for (const cityId of Object.keys(state.cities)) {
    const cName = normalizeNameKey(state.cities[cityId]);
    if (nCity === cName || nCity.includes(cName) || cName.includes(nCity)) {
      return String(cityId);
    }
  }
  return "";
};

export const findStateCityIds = (stateName, cityName, address = "") => {
  const nState = normalizeNameKey(stateName);
  const nCity = normalizeNameKey(cityName);
  const nAddress = normalizeNameKey(address);

  if (nState) {
    for (const key of Object.keys(STATE_CITY)) {
      const state = STATE_CITY[key];
      if (normalizeNameKey(state.state_name) !== nState) continue;
      return {
        stateId: String(state.state_id),
        cityId: matchCityInState(state, nCity),
        stateName: state.state_name,
      };
    }
  }

  if (nAddress) {
    for (const key of Object.keys(STATE_CITY)) {
      const state = STATE_CITY[key];
      const sName = normalizeNameKey(state.state_name);
      if (!sName || !nAddress.includes(sName)) continue;
      return {
        stateId: String(state.state_id),
        cityId: matchCityInState(state, nCity || nAddress),
        stateName: state.state_name,
      };
    }
  }

  return { stateId: "", cityId: "", stateName: "" };
};

export const splitFullName = (fullName) => {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) {
    return { first_name: "", middle_name: "", last_name: "", sur_name: "" };
  }
  if (parts.length === 1) {
    return { first_name: parts[0], middle_name: "", last_name: "", sur_name: "" };
  }
  if (parts.length === 2) {
    return {
      first_name: parts[0],
      middle_name: "",
      last_name: parts[1],
      sur_name: parts[1],
    };
  }
  return {
    first_name: parts[0],
    middle_name: parts.slice(1, -1).join(" "),
    last_name: parts[parts.length - 1],
    sur_name: parts[parts.length - 1],
  };
};

export const normalizeGender = (value) => {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw || raw === "—" || raw === "-") return "";
  if (raw === "M" || raw === "MALE") return "MALE";
  if (raw === "F" || raw === "FEMALE") return "FEMALE";
  if (raw === "O" || raw === "OTHER" || raw === "TRANSGENDER" || raw === "T") {
    return "OTHER";
  }
  return "";
};

/** PHP age check ke liye YYYY-MM-DD. DD/MM/YYYY PHP mein galat parse hota hai. */
export const toApiDob = (value) => {
  const raw = String(value || "").trim();
  if (!raw || raw === "—" || raw === "-") return "";

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const dmy = raw.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (dmy) {
    const first = Number(dmy[1]);
    const second = Number(dmy[2]);
    const year = dmy[3];
    let day = first;
    let month = second;
    if (first <= 12 && second > 12) {
      month = first;
      day = second;
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) return "";
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 8) {
    if (digits.startsWith("19") || digits.startsWith("20")) {
      return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
    }
    return `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
  }

  return "";
};

const aadhaarAddressFromRaw = (raw = {}) => {
  if (raw.address) return String(raw.address).trim();
  return [
    raw.house,
    raw.street,
    raw.landmark,
    raw.locality,
    raw.vtcName,
    raw.district,
    raw.state,
    raw.pincode,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
};

/**
 * Full apply-new payload for POST leadApplication.php
 * Disbursal is manual — backend should not auto-disburse.
 */
export const buildLeadApplicationPayload = ({
  mobile = "",
  email = "",
  pan = "",
  panProfile = null,
  aadhaarNumber = "",
  aadhaarProfile = null,
  aadhaarReference = "",
  locationCoords = {},
  employment = {},
  references = [],
  disburseBank = {},
  customerBankingId = "",
  customerLeadId = "",
  customerStep = 3,
  selfiePhoto = "",
  consent = false,
  aadhaarConsent = false,
  aaMobile = "",
  selectedBankId = "",
} = {}) => {
  const customerMobile = String(mobile || "")
    .replace(/\D/g, "")
    .slice(-10);
  const aadhaar = String(aadhaarNumber || "").replace(/\D/g, "").slice(0, 12);
  const raw = aadhaarProfile?.raw || {};
  const fullName = pickFirst(
    panProfile?.name,
    aadhaarProfile?.name,
    raw.name,
    raw.fullName,
    [raw.firstName, raw.middleName, raw.lastName].filter(Boolean).join(" ")
  );
  const names = splitFullName(fullName);
  const address = pickFirst(
    aadhaarProfile?.address,
    aadhaarAddressFromRaw(raw),
    panProfile?.address
  );
  const stateName = pickFirst(
    raw.state,
    raw.stateName,
    raw.addressState,
    aadhaarProfile?.state
  );
  const cityName = pickFirst(
    raw.district,
    raw.vtcName,
    raw.city,
    raw.districtName,
    aadhaarProfile?.district
  );
  const mappedPlace = findStateCityIds(stateName, cityName, address);
  const stateId = mappedPlace.stateId;
  const cityId = mappedPlace.cityId;
  const pincode = String(
    pickFirst(raw.pincode, raw.pin, aadhaarProfile?.pincode)
  ).replace(/\D/g, "");
  const dob = toApiDob(
    pickFirst(
      raw.dateOfBirth,
      raw.dob,
      raw.date_of_birth,
      raw.birthDate,
      aadhaarProfile?.dobRaw,
      aadhaarProfile?.dob,
      panProfile?.dob
    )
  );
  const gender = normalizeGender(
    pickFirst(
      raw.gender,
      raw.sex,
      aadhaarProfile?.gender,
      panProfile?.gender
    )
  );
  const ref1 = references[0] || {};
  const ref2 = references[1] || {};
  const selfie =
    typeof selfiePhoto === "string" && selfiePhoto.startsWith("data:")
      ? selfiePhoto
      : "";

  return {
    customer_mobile: customerMobile,
    mobile: customerMobile,
    lead_id: compact(customerLeadId),
    step: Number(customerStep) || 3,
    source: "fundswalle",
    lead_data_source_id: 38,
    disbursal_mode: "manual",
    disbursement_type: "manual",
    auto_disbursal: "0",
    term_and_condition: consent || customerMobile.length === 10 ? "YES" : "NO",
    mobile_verified: customerMobile.length === 10 ? "true" : "false",
    pan_verified: panProfile || String(pan || "").length === 10 ? "true" : "false",
    aadhaar_verified: aadhaarProfile || aadhaar.length === 12 ? "true" : "false",
    aadhaar_consent: aadhaarConsent || aadhaar.length === 12 ? "YES" : "NO",

    pan: String(pan || "").toUpperCase(),
    pan_status: panProfile ? true : false,
    pan_fullName: compact(panProfile?.name),
    pan_firstName: compact(panProfile?.firstName || names.first_name),
    pan_lastName: compact(panProfile?.lastName || names.last_name),
    pan_fatherName: compact(panProfile?.fatherName),
    pan_dob: toApiDob(panProfile?.dob),
    pan_gender: normalizeGender(panProfile?.gender),
    pan_address: compact(panProfile?.address),
    pan_aadhaarLinked: compact(panProfile?.aadhaarLinked),
    pan_maskedAadhaarNumber: compact(panProfile?.maskedAadhaarNumber),
    pan_category: compact(panProfile?.category),
    pan_responseCode: compact(panProfile?.responseCode),
    pan_responseMessage: compact(panProfile?.responseMessage),
    pan_referenceId: compact(panProfile?.referenceId),
    pan_verifiedPan: String(pan || "").toUpperCase(),

    first_name: names.first_name,
    middle_name: names.middle_name,
    last_name: names.last_name,
    sur_name: names.sur_name,
    dob,
    gender,
    email_personal: compact(email),
    email: compact(email),

    latitude: compact(locationCoords.latitude),
    longitude: compact(locationCoords.longitude),
    coordinates: compact(locationCoords.coordinates),

    employer: compact(employment.employer),
    employer_name: compact(employment.employer),
    monthly_income: compact(employment.salary),
    salary: compact(employment.salary),
    salary_date: compact(employment.salaryDate),
    loan_amount: compact(employment.loanAmount),
    loan_purpose: compact(employment.loanPurpose),
    employment_type: compact(employment.employmentType),
    salary_mode: compact(employment.salaryMode),

    state_id: stateId ? Number(stateId) : "",
    city: cityId || "",
    city_id: cityId ? Number(cityId) : "",
    state: compact(mappedPlace.stateName || stateName),
    city_name: compact(cityName),
    pincode: compact(pincode),
    pin: compact(pincode),
    address,

    aadhaar_number: aadhaar,
    aadhar_no: aadhaar.slice(-4),
    aadhaar_last_four: aadhaar.slice(-4),
    aadhaar_name: compact(aadhaarProfile?.name),
    aadhaar_dob: compact(dob),
    aadhaar_gender: compact(gender),
    aadhaar_address: address,
    aadhaar_pincode: compact(pincode),
    aadhaar_state: compact(stateName),
    aadhaar_district: compact(cityName),
    aadhaar_referenceId: compact(aadhaarReference),
    aadhaar_mobile: compact(raw.mobile || aaMobile || customerMobile),

    reference1_name: compact(ref1.name),
    reference1_mobile: compact(ref1.mobile),
    reference1_relation: compact(ref1.relation),
    reference2_name: compact(ref2.name),
    reference2_mobile: compact(ref2.mobile),
    reference2_relation: compact(ref2.relation),
    references: JSON.stringify(
      (references || []).map((item) => ({
        name: compact(item?.name),
        mobile: compact(item?.mobile),
        relation: compact(item?.relation),
      }))
    ),

    bank_name: compact(disburseBank.bankName),
    ifsc_code: String(disburseBank.ifsc || "").toUpperCase(),
    account_number: String(disburseBank.accountNumber || "").replace(/\D/g, ""),
    beneficiary_name: compact(disburseBank.holderName),
    account_type: "Savings",
    cust_banking_id: compact(customerBankingId),
    selected_bank_id: compact(selectedBankId),
    aa_mobile: compact(aaMobile),

    selfie: selfie ? "captured" : "",
    selfie_captured: selfie ? "1" : "0",
    selfie_image_url: "",
  };
};

export const parseLeadApplicationResponse = (raw) => {
  const body = raw && typeof raw === "object" ? raw : {};
  const data = body.data && typeof body.data === "object" ? body.data : {};
  const errors = body.errors && typeof body.errors === "object" ? body.errors : {};
  const errorText = Object.values(errors)
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(" ");

  const ok =
    body.success === true ||
    body.status === true ||
    body.Status === 1 ||
    body.status === 1;

  const leadId =
    data.inserted_id ||
    data.lead_id ||
    data.insertedId ||
    body.inserted_id ||
    body.lead_id ||
    "";

  return {
    ok,
    message:
      errorText ||
      String(body.message || body.Message || body.error || ""),
    leadId: leadId ? String(leadId) : "",
    raw: body,
  };
};

export const submitLeadApplication = async (url, payload) => {
  const { selfie_image: _omitSelfie, ...body } = payload || {};
  const resp = await axios.post(url, body, {
    headers: { "Content-Type": "application/json" },
    timeout: 45000,
    validateStatus: () => true,
  });

  if (typeof resp.data === "string" && resp.data.trim().startsWith("<")) {
    return {
      ok: false,
      message: "Server error while saving application. Please try again.",
      leadId: "",
      raw: {},
    };
  }

  return parseLeadApplicationResponse(resp.data);
};
